import IconCheckFeature from '@/components/_rebrand/IconCheckFeature';
import { PageBumper, PageBumperProps } from '@/components/_rebrand/PageBumper';
import ProductAwards, { ProductAwardsProps } from '@/components/_rebrand/ProductAwards';
import TextMediaQuote, { TextMediaQuoteProps } from '@/components/_rebrand/TextMediaQuote';
import Image from '@/components/Image';
import { RequestDemoPageQuery } from '@/graphql';
import RequestDemoPageQueryDocument from '@/graphql/queries/RequestDemoPage.gql';
import ModernRequestDemo from '@/layouts/ModernRequestDemo';
import { ChiliPiperScript } from '@/lib/chili-piper';
import { getStaticPageProps } from '@/lib/next';
import MasterTemplate from '@/templates/MasterTemplate';

const RequestDemoPage: PageTemplate<RequestDemoPageQuery> = () => {
  const AWARDS_PROPS = {
    title: <span>Your team deserves the best. <span className="hidden md:inline"><br /></span> <span className="font-serif">So do your customers.</span></span>,
    description: 'From AI automation to ease of use, Front wins more G2 categories than other top support tools combined. ',
    image:
      <Image
        src="https://front.com/webassets/rebrand/product-awards-image-2025-v2.webp"
        width={1434}
        height={1061}
        alt="Product awards"
        className="block w-full"
      />,

    reviewImage: (
      <Image
        width={172}
        height={36}
        src="https://front.com/webassets/rebrand/icon-review-stars-4.7.svg"
        className="block w-[172px]"
        alt="4.7 stars out of 5"
      />
    ),

    reviewCta: {
      title: '4.7 stars on G2',
      href: 'https://www.g2.com/products/front/reviews',
    },

    awardBadges: [
      <Image
        key="badge-1"
        width={83}
        height={107}
        src="https://front.com/webassets/awards/front-g2-award-momentum-leader-winter-2025.svg"
        className="block w-[83px] md:w-[94px] h-auto"
        alt="g2-front-momentum-leader-winter-2025"
      />,
      <Image
        key="badge-2"
        width={83}
        height={107}
        src="https://front.com/webassets/awards/front-g2-award-most-implementable-winter-2025.svg"
        className="block w-[83px] md:w-[94px]"
        alt="g2-front-most-implementable-winter-2025"
      />,
      <Image
        key="badge-3"
        width={83}
        height={107}
        src="https://front.com/webassets/awards/front-g2-award-best-usability-winter-2025.svg"
        className="block w-[83px] md:w-[94px]"
        alt="g2-front-best-usability-winter-2025"
      />,
      <Image
        key="badge-4"
        width={83}
        height={107}
        src="https://front.com/webassets/awards/front-g2-award-best-support-winter-2025.svg"
        className="block w-[83px] md:w-[94px]"
        alt="g2-front-best-support-winter-2025"
      />,
      <Image
        key="badge-5"
        width={83}
        height={107}
        src="https://front.com/webassets/awards/front-g2-award-overall-leader-winter-2025.svg"
        className="block w-[83px] md:w-[94px]"
        alt="g2-front-overall-leader-winter-2025"
      />,
    ],
  } as ProductAwardsProps;

  const TEXT_MEDIA_QUOTE_PROPS = {
    title: <span><span className="font-serif">Scale exceptional CX </span> without <span className="hidden md:inline"><br /></span>the hassle of legacy help desks.</span>,

    features: [
      {
        icon: <IconCheckFeature className="w-[32px] h-[32px] block" />,
        title: 'Fast, modern, and intuitive',
        description:
          'Front helps your team work smarter—not harder—with a lightning-fast user experience that combines the familiarity of a shared inbox with the efficiency of ticketing.',
        image: <Image src="https://front.com/webassets/rebrand/fast-modern-intuitve-2x.webp" width={612} height={612} alt="Fast, modern, and intuitive" className="w-[295px] h-[295px] md:w-auto md:h-auto" />,
      },
      {
        icon: <IconCheckFeature className="w-[32px] h-[32px] block" />,
        title: 'Tailored to your business',
        description:
          'Design your ideal customer experience. Triage and route inquiries, pull in data, and automate next steps with Front’s easy to update no-code tools.',
        image: <Image src="https://front.com/webassets/rebrand/tailored-to-your-business.webp" width={612} height={612} alt="Tailored to your business" className="w-[295px] h-[295px] md:w-auto md:h-auto" />
      },
      {
        icon: <IconCheckFeature className="w-[32px] h-[32px] block" />,
        title: 'Unified customer journey',
        description:
          'Front brings your team together across email, live chat, SMS, and more for effortless collaboration, smooth escalations, and seamless handoffs.',
        image: <Image src="https://front.com/webassets/rebrand/unified-customer-journey-2x.webp" width={612} height={612} alt="Unified customer journey" className="w-[295px] h-[295px] md:w-auto md:h-auto" />,
      },
    ],
  } as TextMediaQuoteProps;

  const BUMPER_PROPS = {
    title: 'Ready to deliver exceptional service with Front?',
    links: [
      {
        type: 'primary',
        title: 'Request a demo',
        href: '',
        action: 'demo',
      },
      {
        type: 'secondary',
        title: 'Try for free',
        href: '',
        action: 'trial',
      },
    ],
  } as PageBumperProps;

  return (
    <>
      <ModernRequestDemo
        pageName="Request Demo"
      />
      <ProductAwards {...AWARDS_PROPS} />
      <TextMediaQuote {...TEXT_MEDIA_QUOTE_PROPS} />
      <PageBumper {...BUMPER_PROPS} />

      {/* Chili Piper */}
      {ChiliPiperScript}
    </>
  )
};

RequestDemoPage.template = MasterTemplate;

export default RequestDemoPage;

export const getStaticProps = getStaticPageProps('request-demo', async ({ client }) => {
  const props = await client.request<RequestDemoPageQuery>(RequestDemoPageQueryDocument);

  return { props };
});
